/**
 *  @class
 *  @function ShippingEstimator
 *
 *  Para o Brasil (BR): usa a API da Frenet (POST /shipping/quote)
 *  Para outros países: usa a API nativa do Shopify (prepare + async_shipping_rates)
 *
 *  Configurações necessárias no tema (theme.frenet):
 *    window.theme.frenet.token     — token de integração da Frenet
 *    window.theme.frenet.sellerCEP — CEP de origem da loja
 *    window.theme.frenet.items     — array de itens (peso/dimensões)
 *                                    [{ Quantity, Weight, Length, Height, Width, SKU }]
 *    window.theme.frenet.invoiceValue — valor da nota fiscal (R$)
 *
 *  Exemplo de inicialização no seu theme.js ou snippets/frenet-config.liquid:
 *    window.frenetConfig = {
 *      token: "SEU_TOKEN_FRENET",
 *      sellerCEP: "01310100",
 *      invoiceValue: 100,
 *      items: [{ Quantity: 1, Weight: 0.5, Length: 20, Height: 5, Width: 15, SKU: "PRODUTO-001" }]
 *    };
 */
if (!customElements.get('shipping-estimator')) {
  class ShippingEstimator extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.setupCountries();
      this.submitButton = this.querySelector('[type="button"]');
      this.submitButton.addEventListener('click', this.estimateShipping.bind(this));
      this.results = this.querySelector('.shipping-estimator--results');
    }

    async estimateShipping() {
      const zip = this.querySelector('.shipping-estimator-zip').value.replace(/\D/g, ''),
        country = this.querySelector('.shipping-estimator-country').value,
        province = this.querySelector('.shipping-estimator-province').value;

      if (!zip || !country) {
        this.results.innerHTML = `<p style="color:red;">Please include your ZIP code and country.</p>`;
        return;
      }

      this.submitButton.classList.add('loading');
      this.results.innerHTML = '<p>Calculating...</p>';

      try {
        if (country === 'Brazil' || country === 'BR') {
          // ── Brasil: usa API da Frenet ──────────────────────────────────────
          await this.estimateShippingBrazil(zip);
        } else {
          // ── Outros países: usa API nativa do Shopify ───────────────────────
          await this.estimateShippingShopify(zip, country, province);
        }
      } catch (error) {
        console.error('Shipping Estimator Error:', error);
        this.results.innerHTML = `<p style="color:red;">Unexpected error. Try again.</p>`;
      } finally {
        this.submitButton.classList.remove('loading');
      }
    }

    // ──────────────────────────────────────────────────────────────────────────
    // BRASIL — Frenet API
    // ──────────────────────────────────────────────────────────────────────────
    async estimateShippingBrazil(cep) {
      const frenetConfig = window.frenetConfig || {};
      const token = frenetConfig.token || '';
      const sellerCEP = (frenetConfig.sellerCEP || '').replace(/\D/g, '');
      const invoiceValue = frenetConfig.invoiceValue || 1;

      // Tenta obter itens dinâmicos do carrinho se não houver config estática
      let items = frenetConfig.items || null;
      if (!items) {
        items = await this.buildFrenetItemsFromCart();
      }
      if (!items || items.length === 0) {
        // Fallback mínimo para não travar a cotação
        items = [{ Quantity: 1, Weight: 0.3, Length: 16, Height: 5, Width: 11, SKU: 'PRODUTO' }];
      }

      if (!token) {
        this.results.innerHTML = `<p style="color:red;">Token da Frenet não configurado. Contate o administrador da loja.</p>`;
        return;
      }

      const body = {
        SellerCEP: sellerCEP,
        RecipientCEP: cep,
        ShipmentInvoiceValue: invoiceValue,
        ShippingServiceCode: null,
        ShippingItemArray: items,
        RecipientCountry: 'BR'
      };

      const proxyURL = (window.frenetConfig && window.frenetConfig.proxyURL) || 'https://api.frenet.com.br/shipping/quote';
      const res = await fetch(proxyURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-frenet-token': token
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        this.results.innerHTML = `<p style="color:red;">Shipping rating unavaiable for this ZIP code.</p>`;
        return;
      }

      const data = await res.json();
      const services = data.ShippingSevicesArray || [];

      // Filtra serviços com erro ou sem preço
      const validServices = services.filter(
        (s) => !s.Error && s.ShippingPrice > 0
      );

      if (validServices.length === 0) {
        this.results.innerHTML = `<p>${'No shipping options available for this ZIP code.'}</p>`;
        return;
      }

      this.formatFrenetRates(validServices);
    }

    formatFrenetRates(services) {
      const format = window.frenetConfig && window.frenetConfig.moneyFormat ? window.frenetConfig.moneyFormat : 'R$ {{amount}}';

      let listItems = '';
      services.forEach((s) => {
        const price = typeof formatMoney === 'function'
          ? formatMoney(Math.round(s.ShippingPrice * 100), format)
          : `R$ ${s.ShippingPrice.toFixed(2).replace('.', ',')}`;

        const prazo = s.DeliveryTime
          ? ` — ${s.DeliveryTime} business day${s.DeliveryTime > 1 ? 's' : ''}`
          : '';

        listItems += `<li><strong>${s.Carrier} ${s.ServiceDescription}</strong>: ${price}${prazo}</li>`;
      });

      let answer = '';
      if (services.length === 1) {
        answer = 'Your address has one shipping option:';
      } else {
        answer = `Your address has ${services.length} shipping options:`;
      }

      this.results.innerHTML = `<p>${answer}</p><ul>${listItems}</ul>`;
    }

    // Tenta construir os itens do Frenet a partir do carrinho Shopify atual
    async buildFrenetItemsFromCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        if (!cart.items || cart.items.length === 0) return null;

        return cart.items.map((item) => {
          // Tenta pegar dimensões do metafield/propriedade do produto, se disponível
          const weight = item.grams > 0 ? item.grams / 1000 : 0.3; // converte g → kg
          return {
            Quantity: item.quantity,
            Weight: weight,
            Length: item.properties && item.properties._length ? Number(item.properties._length) : 16,
            Height: item.properties && item.properties._height ? Number(item.properties._height) : 5,
            Width: item.properties && item.properties._width ? Number(item.properties._width) : 11,
            SKU: item.sku || item.variant_id.toString()
          };
        });
      } catch (e) {
        console.warn('Frenet: não foi possível ler o carrinho para montar itens.', e);
        return null;
      }
    }

    // ──────────────────────────────────────────────────────────────────────────
    // OUTROS PAÍSES — Shopify nativo
    // ──────────────────────────────────────────────────────────────────────────
    async estimateShippingShopify(zip, country, province) {
      const productForm =
        this.closest('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');
      const variantInput = productForm && productForm.querySelector('[name="id"]');
      const qtyInput = productForm && productForm.querySelector('[name="quantity"]');

      const variantId = variantInput ? Number(variantInput.value) : null;
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10)) : 1;

      if (!variantId) {
        this.results.innerHTML = `<p style="color:red;">Could not detect the selected variant. Please select a size/variant.</p>`;
        return;
      }

      // Adiciona item temporário ao carrinho
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });

      try {
        const prepare = await fetch(
          `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root + 'cart/prepare_shipping_rates' : '/cart/prepare_shipping_rates'}.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}&shipping_address[province]=${encodeURIComponent(province)}`,
          { method: 'POST' }
        );

        if (!prepare.ok) {
          const err = await prepare.json().catch(() => null);
          let msg = 'Unable to retrieve shipping rates.';
          if (err && typeof err === 'object') {
            msg = Object.keys(err).map((k) => `${k} ${err[k]}`).join(', ');
          }
          this.results.innerHTML = `<p style="color:red;">${msg}</p>`;
          return;
        }

        const shippingRates = await this.getShippingRates(zip, country, province);
        if (shippingRates && shippingRates.length > 0) {
          this.formatShippingRates(shippingRates);
        } else {
          this.results.innerHTML = `<p>${'No shipping options available for this address.'}</p>`;
        }
      } finally {
        // Remove item temporário do carrinho
        try {
          const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          const cartData = await cartRes.json();
          const lastItem = cartData.items[cartData.items.length - 1];
          if (lastItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: lastItem.key, quantity: 0 })
            });
          }
        } catch (e) {
          console.warn('Could not remove temporary cart item:', e);
        }
      }
    }

    async getShippingRates(zip, country, province) {
      const res = await fetch(
        `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root + 'cart/async_shipping_rates' : '/cart/async_shipping_rates'}.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}&shipping_address[province]=${encodeURIComponent(province)}`
      );
      const text = await res.text();
      if (text === 'null') {
        await new Promise((r) => setTimeout(r, 1000));
        return this.getShippingRates(zip, country, province);
      } else {
        return JSON.parse(text).shipping_rates;
      }
    }

    formatShippingRates(shippingRates) {
      let formattedShippingRates = '',
        answer = '',
        format = window.frenetConfig && window.frenetConfig.moneyFormat ? window.frenetConfig.moneyFormat : '${{amount}}';
      shippingRates.forEach((shippingRate) => {
        formattedShippingRates += `<li>${shippingRate.presentment_name}: ${formatMoney(parseFloat(shippingRate.price) * 100, format)}</li>`;
      });
      if (shippingRates.length === 0) {
        answer = 'No shipping options available.';
      } else if (shippingRates.length === 1) {
        answer = 'Your address has one shipping option:';
      } else {
        answer = `Your address has ${shippingRates.length} shipping options:`;
      }
      this.results.innerHTML = `<p>${answer}</p>${formattedShippingRates ? `<ul>${formattedShippingRates}</ul>` : ''}`;
    }

    setupCountries() {
      if (Shopify && Shopify.CountryProvinceSelector) {
        let formId = this.dataset.formId;
        new Shopify.CountryProvinceSelector(
          `shipping-estimator-country-${formId}`,
          `shipping-estimator-province-${formId}`,
          { hideElement: `shipping-estimator-province-wrapper-${formId}` }
        );
      }
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);
}

// --- Inicialização manual do componente (corrige bug no tema publicado) ---
document.addEventListener('DOMContentLoaded', () => {
  const estimators = document.querySelectorAll('shipping-estimator');
  estimators.forEach((estimator) => {
    if (typeof estimator.connectedCallback === 'function') {
      estimator.connectedCallback();
    }
  });
});
